import React, { useState } from "react";
import { message } from "antd";
import { ArrowRight } from "lucide-react";
import { quizIcons } from "./svAssets";

const FREQUENCY = ["Never", "Rarely", "Sometimes", "Often", "Very frequently"].map((label) => ({ label }));

// Option order matters: the index is normalised to 0–1 for the /smartvital payload.
const questions = [
  {
    id: 1,
    category: "BODY",
    question: "How often do you experience physical pain or discomfort without a clear cause (e.g., headaches, body pain, muscle aches)?",
    options: FREQUENCY,
  },
  {
    id: 2,
    category: "ENERGY",
    question: "How often do you feel physically exhausted or drained, even after a normal day of work or activity?",
    options: FREQUENCY,
  },
  {
    id: 3,
    category: "SLEEP",
    question: "How well do you sleep at night? (i.e., feel rested upon waking up)",
    options: [
      { label: "Very well", hint: "restful sleep, wake up refreshed" },
      { label: "Mostly well", hint: "but sometimes restless" },
      { label: "Average", hint: "often wake up tired" },
      { label: "Poor", hint: "wake up multiple times during the night" },
      { label: "Very poor", hint: "feel unrested throughout the day" },
    ],
  },
  {
    id: 4,
    category: "FOCUS",
    question: "In the past two weeks, how often have you experienced difficulty concentrating, making decisions, or remembering things?",
    options: FREQUENCY,
  },
  {
    id: 5,
    category: "DAILY LOAD",
    question: "Do you feel overwhelmed by daily tasks or responsibilities?",
    options: FREQUENCY,
  },
  {
    id: 6,
    category: "APPETITE",
    question: "Have you noticed any changes in your appetite (e.g., eating more or less than usual)?",
    options: ["No change", "Slight decrease", "Slight increase", "Significant decrease", "Significant increase"].map((label) => ({ label })),
  },
  {
    id: 7,
    category: "WORRY",
    question: "How often do you find yourself feeling anxious, worried, or nervous without a clear reason?",
    options: FREQUENCY,
  },
  {
    id: 8,
    category: "LIFE EVENTS",
    question: "Have you experienced any recent life changes or challenges that might be affecting your health (e.g., work pressure, family issues, loss)?",
    options: [
      { label: "No" },
      { label: "Yes", hint: "but only minor challenges" },
      { label: "Yes", hint: "moderate challenges" },
      { label: "Yes", hint: "major challenges" },
      { label: "Yes", hint: "overwhelming or traumatic challenges" },
    ],
  },
];

// Signal-strength icon: more filled bars = needs more care (level 0–4).
export const SignalBars = ({ level }) => (
  <img className="sv-bars" src={quizIcons.levels[level]} alt="" aria-hidden="true" />
);

function QuizComponent({ onQuizSubmit, onSkip }) {
  const [answers, setAnswers] = useState({});
  // Set when the user tries to submit with unanswered questions — drives the inline
  // message and the red outline on the cards still missing an answer.
  const [showMissing, setShowMissing] = useState(false);
  const answeredCount = Object.keys(answers).length;
  const pct = Math.round((answeredCount / questions.length) * 100);

  const pick = (qid, idx) => setAnswers((prev) => ({ ...prev, [qid]: idx }));

  const handleSubmit = () => {
    const firstOpen = questions.find((q) => !(q.id in answers));
    if (firstOpen) {
      setShowMissing(true);
      message.error("Please answer all questions before submitting.");
      document.getElementById(`sv-q-${firstOpen.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    const calculatedAnswers = questions.map((q) => Number((answers[q.id] / (q.options.length - 1)).toFixed(1)));
    sessionStorage.setItem("calculatedAnswers", JSON.stringify(calculatedAnswers));
    onQuizSubmit?.();
  };

  return (
    <>
      <div className="sv-quiz-grid">
        {questions.map((q) => {
          const selected = answers[q.id];
          const answered = selected !== undefined;
          const missing = showMissing && !answered;
          return (
            <div key={q.id} id={`sv-q-${q.id}`} className={`sv-card sv-q${answered ? " is-answered" : ""}${missing ? " is-missing" : ""}`}>
              {answered && <img className="sv-q-done" src={quizIcons.check} alt="Answered" />}
              <div className="sv-q-head">
                <img className="sv-q-icon" src={quizIcons.questions[q.id - 1]} alt="" />
                <div>
                  <p className="sv-q-meta">
                    Question {q.id} of {questions.length} · {q.category}
                  </p>
                  <p className="sv-q-text">{q.question}</p>
                </div>
              </div>
              <div className="sv-q-opts" role="radiogroup" aria-label={q.question}>
                {q.options.map((opt, idx) => (
                  <button
                    key={idx}
                    type="button"
                    role="radio"
                    aria-checked={selected === idx}
                    className={`sv-q-opt${selected === idx ? " is-on" : ""}`}
                    onClick={() => pick(q.id, idx)}
                  >
                    <SignalBars level={idx} />
                    <span className="sv-q-opt-label">{opt.label}</span>
                    {opt.hint && <span className="sv-q-opt-hint">({opt.hint})</span>}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="sv-quiz-bar">
        <div className="sv-legend">
          Feeling good <SignalBars level={4} /> Needs care
        </div>
        <div className="sv-progress">
          <div
            className="sv-progress-ring"
            style={{ background: `conic-gradient(var(--sv-green) ${pct}%, #E4ECF3 0)` }}
          >
            <span>
              {answeredCount}/{questions.length}
            </span>
          </div>
          <div>
            <p className="sv-progress-title">
              {answeredCount} of {questions.length} answered
            </p>
            <p className="sv-progress-sub">Face scan opens next</p>
          </div>
        </div>
        <div className="sv-quiz-actions">
          {showMissing && answeredCount < questions.length && (
            <p className="sv-quiz-error" role="alert">
              Please answer all {questions.length} questions to continue ({questions.length - answeredCount} remaining).
            </p>
          )}
          <button type="button" className="sv-link" onClick={onSkip}>
            Skip to face scan
          </button>
          <button type="button" className="sv-btn sv-btn-primary" onClick={handleSubmit}>
            Submit and start face scan <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </>
  );
}

export default QuizComponent;
